import { useLayoutEffect, useState, type RefObject } from 'react';

import { getFullyVisibleCount } from '../utils/railBatches';

type UseRailFullyVisibleCountReturn = {
  trackRef: (node: HTMLElement | null) => void;
  fullyVisibleCount: number | null;
};

const measureFullyVisibleCount = (viewport: HTMLElement, track: HTMLElement): number | null => {
  const slideWidth = track.firstElementChild?.getBoundingClientRect().width ?? 0;
  if (slideWidth === 0) return null;

  return getFullyVisibleCount({
    railWidth: viewport.clientWidth,
    slideWidth,
    gapWidth: parseFloat(getComputedStyle(track).columnGap) || 0,
  });
};

export const useRailFullyVisibleCount = (
  viewportRef: RefObject<HTMLElement>
): UseRailFullyVisibleCountReturn => {
  const [track, setTrack] = useState<HTMLElement | null>(null);
  const [fullyVisibleCount, setFullyVisibleCount] = useState<number | null>(null);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (track === null || viewport === null) return undefined;

    const measure = () => {
      const count = measureFullyVisibleCount(viewport, track);
      if (count !== null) setFullyVisibleCount(count);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [track, viewportRef]);

  return { trackRef: setTrack, fullyVisibleCount };
};
